import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, AlertCircle } from 'lucide-react';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';

interface Deadline {
  id: string;
  title: string;
  date: Date;
  type: 'goal' | 'application' | 'activity' | 'academic';
  priority: 'high' | 'medium' | 'low';
}

const mockDeadlines: Deadline[] = [
  {
    id: '1',
    title: 'Science Fair Project Submission',
    date: new Date(2024, 2, 15),
    type: 'academic',
    priority: 'high',
  },
  {
    id: '2',
    title: 'Complete 50 Volunteer Hours Goal',
    date: new Date(2024, 2, 20),
    type: 'goal',
    priority: 'medium',
  },
  {
    id: '3',
    title: 'Summer Program Application',
    date: new Date(2024, 2, 28),
    type: 'application',
    priority: 'high',
  },
];

export const UpcomingDeadlines: React.FC = () => {
  const getDaysUntil = (date: Date) => {
    const today = new Date();
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'academic':
        return Calendar;
      case 'goal':
        return AlertCircle;
      case 'application':
        return Clock;
      case 'activity':
        return Calendar;
      default:
        return Calendar;
    }
  };

  return (
    <Card>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Deadlines</h3>
      <div className="space-y-3">
        {mockDeadlines.map((deadline, index) => {
          const Icon = getTypeIcon(deadline.type);
          const daysUntil = getDaysUntil(deadline.date);

          return (
            <motion.div
              key={deadline.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white rounded-lg">
                  <Icon size={16} className="text-gray-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{deadline.title}</h4>
                  <p className="text-sm text-gray-500">
                    {deadline.date.toLocaleDateString()} ({daysUntil} days)
                  </p>
                </div>
              </div>
              <Badge variant={getPriorityColor(deadline.priority)} size="sm">
                {deadline.priority}
              </Badge>
            </motion.div>
          );
        })}
      </div>
    </Card>
  );
};